<template>
  <div class="brep-page py-3">
    <header class="brep-toolbar">
      <div class="brep-toolbar__title">
        <span class="brep-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Branch Report</h1>
          <p>Download a detailed multi-sheet Excel with every branch metric the Analytics page surfaces.</p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="brep-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="brep-alert">{{ successMessage }}</CAlert>

    <section class="brep-section">
      <header class="brep-section__head">
        <h2>Date Range</h2>
        <small class="brep-section__hint">{{ rangeLabel }}</small>
      </header>
      <div class="brep-section__body">
        <div class="brep-filterbar">
          <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          <div class="brep-presets" role="group" aria-label="Quick date ranges">
            <button
              v-for="preset in presets"
              :key="preset.key"
              type="button"
              class="brep-preset"
              :class="{ 'is-active': activePreset === preset.key }"
              @click="applyPreset(preset.key)"
            >
              {{ preset.label }}
            </button>
          </div>
          <span class="brep-filterbar__spacer"></span>
          <button
            type="button"
            class="brep-download-btn"
            :class="{ 'is-loading': isDownloading }"
            :disabled="isDownloading"
            @click="download"
          >
            <svg v-if="!isDownloading" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>{{ isDownloading ? 'Generating…' : 'Download Excel' }}</span>
          </button>
        </div>
      </div>
    </section>

    <section class="brep-section">
      <header class="brep-section__head">
        <h2>What's inside</h2>
        <small class="brep-section__hint">{{ sheets.length }} sheets · scoped to the date range above</small>
      </header>
      <div class="brep-section__body">
        <ol class="brep-sheets">
          <li
            v-for="(s, idx) in sheets"
            :key="s.title"
            class="brep-sheet"
          >
            <span class="brep-sheet__num">{{ idx + 1 }}</span>
            <div class="brep-sheet__body">
              <span class="brep-sheet__title">{{ s.title }}</span>
              <span class="brep-sheet__hint">{{ s.hint }}</span>
            </div>
          </li>
        </ol>
        <p class="brep-foot-note">
          Workbook is filtered to the three transacting organisations
          (Drugs / Agropharma / Vet Pharma) — same scope as the Analytics
          page on screen.
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import DateRangePicker from '@/components/DateRangePicker.vue'

const VISIBLE_ORG_IDS = [1, 2, 4]

export default {
  name: 'BranchReport',
  components: { DateRangePicker },
  data () {
    const now = new Date()
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    return {
      fromDate: today,
      toDate: today,
      isDownloading: false,
      errorMessage: '',
      successMessage: '',
      presets: [
        { key: 'today', label: 'Today' },
        { key: '7d', label: 'Last 7 Days' },
        { key: '30d', label: 'Last 30 Days' },
        { key: 'mtd', label: 'This Month' },
        { key: 'last-month', label: 'Last Month' },
        { key: 'ytd', label: 'This Year' }
      ],
      sheets: [
        { title: 'Overview', hint: 'KPI totals + per-organisation rollup' },
        { title: 'By Branch', hint: 'Every branch — per-org sales, total orders, returns, return value' },
        { title: 'By State', hint: 'State rollup with branch count and per-org sales' },
        { title: 'Staff', hint: 'Every staff with branch, orders, items, returns, return rate, sales' },
        { title: 'Items', hint: 'Item-wise sold qty, sales, returned qty, return value' },
        { title: 'Returns', hint: 'Every returned order with credit note, branch, staff, refund amount' },
        { title: 'Roles', hint: 'Role-wise orders, items, returns, sales' },
        { title: 'Shipping', hint: 'Orders + sales grouped by shipping status' }
      ]
    }
  },
  computed: {
    dateRange: {
      get () { return { fromDate: this.fromDate, toDate: this.toDate } },
      set (value) {
        this.fromDate = (value && value.fromDate) || ''
        this.toDate = (value && value.toDate) || ''
      }
    },
    rangeLabel () {
      if (!this.fromDate || !this.toDate) return ''
      if (this.fromDate === this.toDate) return this.formatDisplay(this.fromDate)
      return `${this.formatDisplay(this.fromDate)} → ${this.formatDisplay(this.toDate)}`
    },
    activePreset () {
      if (!this.fromDate || !this.toDate) return ''
      for (const preset of this.presets) {
        const range = this.rangeForPreset(preset.key)
        if (range && range.fromDate === this.fromDate && range.toDate === this.toDate) {
          return preset.key
        }
      }
      return ''
    }
  },
  methods: {
    formatDisplay (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      if (!y || !m || !d) return iso
      return `${d}-${m}-${y}`
    },
    toIso (date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    rangeForPreset (key) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const start = new Date(today)
      const end = new Date(today)
      switch (key) {
        case 'today':
          break
        case '7d':
          start.setDate(today.getDate() - 6)
          break
        case '30d':
          start.setDate(today.getDate() - 29)
          break
        case 'mtd':
          start.setDate(1)
          break
        case 'last-month':
          start.setMonth(today.getMonth() - 1, 1)
          end.setMonth(today.getMonth(), 0)
          break
        case 'ytd':
          start.setMonth(0, 1)
          break
        default:
          return null
      }
      return { fromDate: this.toIso(start), toDate: this.toIso(end) }
    },
    applyPreset (key) {
      const range = this.rangeForPreset(key)
      if (!range) return
      this.fromDate = range.fromDate
      this.toDate = range.toDate
    },
    download () {
      this.errorMessage = ''
      this.successMessage = ''
      if (!this.fromDate || !this.toDate) {
        this.errorMessage = 'Please pick a date range first.'
        return
      }
      if (this.fromDate > this.toDate) {
        this.errorMessage = 'From date must be on or before to date.'
        return
      }

      this.isDownloading = true
      const token = localStorage.getItem('api_token')

      axios({
        url: `${this.$apiAdress}/api/branch/report-xlsx?token=${token}`,
        method: 'POST',
        data: {
          fromDate: this.fromDate,
          toDate: this.toDate,
          organisation_ids: VISIBLE_ORG_IDS
        },
        responseType: 'blob'
      })
        .then((response) => {
          const blob = new Blob(
            [response.data],
            { type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          )
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `branch_report_${this.fromDate}_to_${this.toDate}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          this.successMessage = 'Branch report downloaded.'
          setTimeout(() => { this.successMessage = '' }, 3000)
        })
        .catch(async (error) => {
          const status = error?.response?.status
          let detail = ''
          const blob = error?.response?.data
          if (blob && typeof blob.text === 'function') {
            try {
              const text = await blob.text()
              try {
                const parsed = JSON.parse(text)
                detail = parsed.message || parsed.error || parsed.exception || text
              } catch (e) { detail = text }
            } catch (e) { /* ignore */ }
          } else if (error?.message) {
            detail = error.message
          }
          if (detail && detail.length > 300) detail = detail.slice(0, 300) + '…'
          const prefix = status ? `Unable to download branch report (HTTP ${status})` : 'Unable to download branch report.'
          this.errorMessage = detail ? `${prefix}: ${detail}` : prefix
        })
        .finally(() => { this.isDownloading = false })
    }
  }
}
</script>

<style scoped>
.brep-page { padding: 0.25rem 0; }

.brep-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.75rem; padding: 0.25rem 0.25rem 1rem; flex-wrap: wrap;
}
.brep-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.brep-toolbar__accent {
  width: 4px; height: 36px; border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}
.brep-toolbar h1 {
  margin: 0; font-size: 1.15rem; font-weight: 700;
  color: #1f2a44; letter-spacing: -0.01em; line-height: 1.2;
}
.brep-toolbar p { margin: 0; font-size: 0.78rem; color: #6b7280; }

.brep-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.brep-section {
  background: #ffffff; border: 1px solid #e5e9f2;
  border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}
.brep-section__head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.65rem; padding: 0.65rem 1rem;
  border-bottom: 1px solid #eef1f7; background: #fbfcfe;
  border-top-left-radius: 12px; border-top-right-radius: 12px;
}
.brep-section__head h2 {
  margin: 0; font-size: 0.92rem; font-weight: 700;
  color: #1f2a44; letter-spacing: -0.01em;
}
.brep-section__hint { font-size: 0.74rem; color: #6b7280; }
.brep-section__body { padding: 1rem; }

.brep-filterbar {
  display: flex; align-items: center; flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
}
.brep-filterbar__spacer { flex: 1 1 auto; min-width: 0.5rem; }
.brep-filterbar >>> .date-range--form { width: auto; }
.brep-filterbar >>> .date-range--form .date-range-anchor { width: auto; }
.brep-filterbar >>> .date-range--form .date-range-shell { width: auto; min-width: 280px; }

.brep-presets { display: flex; gap: 0.3rem; flex-wrap: wrap; align-items: center; }
.brep-preset {
  height: 28px; padding: 0 0.7rem;
  border: 1px solid #e5e9f2; border-radius: 999px;
  background: #ffffff; color: #4b5563;
  font-size: 0.72rem; font-weight: 600; cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.brep-preset:hover { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.brep-preset.is-active {
  border-color: #16a07a; color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}
.brep-preset:focus { outline: none; }
.brep-preset:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.brep-download-btn {
  display: inline-flex; align-items: center; gap: 0.45rem;
  height: 36px; padding: 0 1.1rem; border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0; color: #ffffff;
  font-family: inherit; font-size: 0.85rem; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.brep-download-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}
.brep-download-btn:disabled { opacity: 0.7; cursor: progress; }
.brep-download-btn:focus { outline: none; }
.brep-download-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.brep-sheets {
  list-style: none; padding: 0; margin: 0;
  display: grid; gap: 0.5rem;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}
.brep-sheet {
  display: flex; align-items: flex-start; gap: 0.6rem;
  padding: 0.6rem 0.75rem;
  background: #fbfcfe; border: 1px solid #eef1f7; border-radius: 10px;
}
.brep-sheet__num {
  flex: 0 0 24px; height: 24px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 6px;
  background: #ecfdf5; color: #0e5b46;
  font-size: 0.7rem; font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.brep-sheet__body { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.brep-sheet__title {
  font-size: 0.85rem; font-weight: 700; color: #1f2a44;
}
.brep-sheet__hint {
  font-size: 0.72rem; color: #6b7280; line-height: 1.35;
}

.brep-foot-note {
  margin: 0.85rem 0 0; padding: 0.5rem 0.75rem;
  font-size: 0.72rem; color: #6b7280;
  background: #fbfcfe; border: 1px solid #eef1f7; border-radius: 8px;
}
</style>
